import React, { useId } from 'react';

export type AiSparkleIconProps = {
  size?: number;
  className?: string;
  /** gradient = blue/purple AI sparkle; white = on dark or colored surfaces */
  variant?: 'gradient' | 'white';
};

/** Concave 4-point sparkle (trendy generate-AI / sparkle icon style). */
function concaveSparkle(cx: number, cy: number, r: number): string {
  const c = r * 0.24;
  return [
    `M ${cx} ${cy - r}`,
    `C ${cx + c} ${cy - c} ${cx + c} ${cy - c} ${cx + r} ${cy}`,
    `C ${cx + c} ${cy + c} ${cx + c} ${cy + c} ${cx} ${cy + r}`,
    `C ${cx - c} ${cy + c} ${cx - c} ${cy + c} ${cx - r} ${cy}`,
    `C ${cx - c} ${cy - c} ${cx - c} ${cy - c} ${cx} ${cy - r}`,
    'Z',
  ].join(' ');
}

/** Primary bottom-right, secondary top-left, tertiary far-left (Shutterstock-style cluster). */
const SPARKLES = [
  { cx: 16.4, cy: 16.2, r: 4.1 },
  { cx: 8.6, cy: 7.4, r: 2.75 },
  { cx: 4.1, cy: 11.6, r: 1.65 },
] as const;

const AiSparkleIcon: React.FC<AiSparkleIconProps> = ({
  size = 24,
  className,
  variant = 'gradient',
}) => {
  const gid = useId().replace(/:/g, '');
  const gradId = `ai-sparkle-grad-${gid}`;
  const fill = variant === 'white' ? '#ffffff' : `url(#${gradId})`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      {variant === 'gradient' && (
        <defs>
          <linearGradient id={gradId} x1="2" y1="22" x2="22" y2="2" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#7C3AED" />
            <stop offset="100%" stopColor="#A78BFA" />
          </linearGradient>
        </defs>
      )}
      <g fill={fill}>
        {SPARKLES.map((s) => (
          <path key={`${s.cx}-${s.cy}`} d={concaveSparkle(s.cx, s.cy, s.r)} />
        ))}
      </g>
    </svg>
  );
};

export default AiSparkleIcon;
